import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Truck } from "lucide-react";

const SCHEDULE_LABEL = {
  primary: "Chosen delivery date",
  mon_wed: "Mon–Wed window (date TBC)",
  wed_fri: "Wed–Fri window (date TBC)",
};

const STATUS_STYLE = {
  pending: "bg-gray-200 text-gray-700",
  confirmed: "bg-blue-100 text-blue-800",
  delivered: "bg-emerald-100 text-emerald-800",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * View-only modal for a single order. Used by the admin Orders tab.
 */
export function OrderDetailModal({ open, onOpenChange, order }) {
  if (!order) return null;

  const items = order.items || [];
  const itemLookup = Object.fromEntries(items.map((it) => [it.item_id, it]));
  const groups = order.delivery_groups || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col p-0" data-testid="order-detail-modal">
        <DialogHeader className="p-5 pb-3 border-b flex-shrink-0">
          <DialogTitle className="flex items-center gap-3 pr-8">
            <span className="font-mono text-sm">
              {order.order_number || order.id?.slice(0, 8)}
            </span>
            <span
              className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                STATUS_STYLE[order.status] || STATUS_STYLE.pending
              }`}
            >
              {order.status || "pending"}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto p-5 space-y-5 text-sm">
          {/* Customer */}
          <section>
            <h4 className="font-semibold text-gray-700 mb-2">Customer</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-gray-700">
              <div><span className="text-gray-400">Name:</span> {order.customer_name}</div>
              <div><span className="text-gray-400">Email:</span> {order.customer_email}</div>
              <div><span className="text-gray-400">Phone:</span> {order.customer_phone}</div>
              <div><span className="text-gray-400">Location:</span> {order.location_slug || "guest"}</div>
              <div className="sm:col-span-2">
                <span className="text-gray-400">Address:</span> {order.delivery_address}
              </div>
            </div>
          </section>

          {/* Dates */}
          <section>
            <h4 className="font-semibold text-gray-700 mb-2">Timing</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-gray-700">
              <div>
                <span className="text-gray-400">Order placed:</span> {formatDate(order.created_at)}
              </div>
              <div>
                <span className="text-gray-400">Preferred delivery date:</span>{" "}
                {order.delivery_date || "—"}
              </div>
              {order.delivery_week && (
                <div>
                  <span className="text-gray-400">Menu week:</span> Week {order.delivery_week}
                </div>
              )}
            </div>
          </section>

          {/* Delivery schedule */}
          {groups.length > 0 && (
            <section>
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Truck className="w-4 h-4 text-[#1aa59a]" />
                Delivery schedule ({groups.length} group{groups.length === 1 ? "" : "s"})
              </h4>
              <div className="space-y-2">
                {groups.map((g, idx) => {
                  const primary = g.schedule === "primary";
                  return (
                    <div
                      key={`${g.schedule}-${idx}`}
                      className={`border-l-4 pl-3 py-2 rounded-r ${
                        primary
                          ? "border-[#1aa59a] bg-emerald-50/50"
                          : "border-amber-400 bg-amber-50/50"
                      }`}
                      data-testid={`order-group-${g.schedule}`}
                    >
                      <div className="text-xs font-bold text-gray-800">
                        Delivery {idx + 1} · {SCHEDULE_LABEL[g.schedule] || g.schedule}
                      </div>
                      <div
                        className={`text-xs ${primary ? "text-[#1aa59a]" : "text-amber-700"}`}
                      >
                        {primary && g.date ? g.date : g.day_hint || "date TBC"}
                      </div>
                      <ul className="mt-1 text-xs text-gray-700 list-disc list-inside">
                        {(g.item_ids || []).map((id) => {
                          const it = itemLookup[id];
                          if (!it) return null;
                          return (
                            <li key={id}>
                              {it.quantity}× {it.name}
                              {it.variant ? ` (${it.variant})` : ""}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* All items */}
          <section>
            <h4 className="font-semibold text-gray-700 mb-2">All items</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500 uppercase">
                  <tr>
                    <th className="text-left p-2">Item</th>
                    <th className="text-center p-2">Qty</th>
                    <th className="text-right p-2">Unit</th>
                    <th className="text-right p-2">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-2">
                        {it.name}
                        {it.variant ? (
                          <span className="text-[#1aa59a]"> ({it.variant})</span>
                        ) : null}
                        {it.week_number ? (
                          <span className="text-blue-600"> · W{it.week_number}</span>
                        ) : null}
                      </td>
                      <td className="p-2 text-center">{it.quantity}</td>
                      <td className="p-2 text-right font-mono">£{Number(it.price).toFixed(2)}</td>
                      <td className="p-2 text-right font-mono">
                        £{(Number(it.price) * Number(it.quantity)).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t bg-gray-50 font-bold">
                    <td className="p-2 text-right" colSpan={3}>
                      Total
                    </td>
                    <td className="p-2 text-right text-[#1aa59a]">
                      £{Number(order.total_amount || 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {/* Notes */}
          {order.notes ? (
            <section>
              <h4 className="font-semibold text-gray-700 mb-2">Notes</h4>
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-gray-800 whitespace-pre-wrap text-sm">
                {order.notes}
              </div>
            </section>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
